/**
 * @file 岗位与组员职阶常量
 * @module constants/post
 * @description 统一维护岗位配置、职阶枚举、标签类型和岗位权限判断工具。
 */
import {
  Picture,
  EditPen,
  View,
  Monitor,
  DocumentChecked,
  Timer,
  KnifeFork,
  Upload
} from '@element-plus/icons-vue'
import { MANGA_CATEGORY_CONFIG } from '@/constants/manga'

/**
 * 默认岗位上传文件大小限制，单位 MB。
 */
const DEFAULT_FILE_SIZE_LIMIT = 1 // 1MB

/**
 * 岗位配置，统一管理岗位编号、展示文案、接口标识、图标和上传限制。
 */
export const POST_CONFIG = {
  /**
   * 图源岗位配置；后端岗位表无该岗位，单话流程单独使用。
   */
  PROVIDER: {
    /** 岗位编号，用于前端流程排序。 */
    id: 0,
    /** 岗位展示名称。 */
    label: '图源',
    /** 岗位枚举键，兼容旧版常量映射。 */
    key: 'PROVIDER',
    /** 岗位接口标识，用于上传、下载和进度判断。 */
    name: 'provider',
    /** 岗位在界面中使用的 Element Plus 图标。 */
    icon: Picture,
    /** 该岗位允许上传的最大文件大小，单位 MB。 */
    maxFileSize: 500 // 500MB
  },
  /**
   * 翻译岗位配置。
   */
  TRANSLATOR: {
    /** 岗位编号，对应后端翻译岗位。 */
    id: 1,
    /** 岗位展示名称。 */
    label: '翻译',
    /** 岗位枚举键，兼容旧版常量映射。 */
    key: 'TRANSLATOR',
    /** 岗位接口标识，用于上传、下载和进度判断。 */
    name: 'translator',
    /** 岗位在界面中使用的 Element Plus 图标。 */
    icon: EditPen,
    /** 该岗位允许上传的最大文件大小，单位 MB。 */
    maxFileSize: DEFAULT_FILE_SIZE_LIMIT
  },
  /**
   * 校对岗位配置。
   */
  PROOFREADER: {
    /** 岗位编号，对应后端校对岗位。 */
    id: 2,
    /** 岗位展示名称。 */
    label: '校对',
    /** 岗位枚举键，兼容旧版常量映射。 */
    key: 'PROOFREADER',
    /** 岗位接口标识，用于上传、下载和进度判断。 */
    name: 'proofreader',
    /** 岗位在界面中使用的 Element Plus 图标。 */
    icon: View,
    /** 该岗位允许上传的最大文件大小，单位 MB。 */
    maxFileSize: DEFAULT_FILE_SIZE_LIMIT
  },
  /**
   * 嵌字岗位配置。
   */
  LETTERER: {
    /** 岗位编号，对应后端嵌字岗位。 */
    id: 3,
    /** 岗位展示名称。 */
    label: '嵌字',
    /** 岗位枚举键，兼容旧版常量映射。 */
    key: 'LETTERER',
    /** 岗位接口标识，用于上传、下载和进度判断。 */
    name: 'letterer',
    /** 岗位在界面中使用的 Element Plus 图标。 */
    icon: Monitor,
    /** 该岗位允许上传的最大文件大小，单位 MB。 */
    maxFileSize: 500 // 500MB
  },
  /**
   * 审稿岗位配置。
   */
  REVIEWER: {
    /** 岗位编号，对应后端审稿岗位。 */
    id: 4,
    /** 岗位展示名称。 */
    label: '审稿',
    /** 岗位枚举键，兼容旧版常量映射。 */
    key: 'REVIEWER',
    /** 岗位接口标识，用于上传、下载和进度判断。 */
    name: 'reviewer',
    /** 岗位在界面中使用的 Element Plus 图标。 */
    icon: DocumentChecked,
    /** 该岗位允许上传的最大文件大小，单位 MB。 */
    maxFileSize: DEFAULT_FILE_SIZE_LIMIT
  },
  /**
   * 其他岗位配置。
   */
  OTHER: {
    /** 岗位编号，对应后端其他岗位。 */
    id: 5,
    /** 岗位展示名称。 */
    label: '其他',
    /** 岗位枚举键，兼容旧版常量映射。 */
    key: 'OTHER',
    /** 岗位接口标识，用于岗位展示和兼容判断。 */
    name: 'other',
    /** 岗位在界面中使用的 Element Plus 图标。 */
    icon: KnifeFork,
    /** 该岗位允许上传的最大文件大小，单位 MB。 */
    maxFileSize: DEFAULT_FILE_SIZE_LIMIT
  },
  /**
   * 时轴岗位配置，动画类漫画流程使用。
   */
  TIMER: {
    /** 岗位编号，对应后端时轴岗位。 */
    id: 6,
    /** 岗位展示名称。 */
    label: '时轴',
    /** 岗位枚举键，兼容旧版常量映射。 */
    key: 'TIMER',
    /** 岗位接口标识，用于上传、下载和进度判断。 */
    name: 'timer',
    /** 岗位在界面中使用的 Element Plus 图标。 */
    icon: Timer,
    /** 该岗位允许上传的最大文件大小，单位 MB。 */
    maxFileSize: DEFAULT_FILE_SIZE_LIMIT
  },
  /**
   * 发布岗位配置。
   */
  PUBLISHER: {
    /** 岗位编号，对应发布流程。 */
    id: 7,
    /** 岗位展示名称。 */
    label: '发布',
    /** 岗位枚举键，兼容旧版常量映射。 */
    key: 'PUBLISHER',
    /** 岗位接口标识，用于发布流程展示。 */
    name: 'publish',
    /** 岗位在界面中使用的 Element Plus 图标。 */
    icon: Upload,
    /** 该岗位允许上传的最大文件大小，单位 MB。 */
    maxFileSize: DEFAULT_FILE_SIZE_LIMIT
  }
} as const

/**
 * 岗位配置对象的顶层键名类型。
 */
export type PostKey = keyof typeof POST_CONFIG

/**
 * 单个岗位配置项类型。
 */
export type PostConfig = (typeof POST_CONFIG)[PostKey]

/**
 * 岗位接口标识类型。
 */
export type PostName = PostConfig['name']

/**
 * 常驻岗位列表，包含翻译、校对、嵌字、审稿和时轴岗位。
 */
export const STATIONED_POST = [
  POST_CONFIG.TRANSLATOR,
  POST_CONFIG.PROOFREADER,
  POST_CONFIG.LETTERER,
  POST_CONFIG.REVIEWER,
  POST_CONFIG.TIMER
] as const

/**
 * 有权限发布评价的岗位ID列表
 * 只有校对(2)和审稿(4)可以发布评价
 */
export const EVALUATION_AUTHORIZED_POSTS: readonly number[] = [
  POST_CONFIG.PROOFREADER.id,
  POST_CONFIG.REVIEWER.id
]

/**
 * 可发布评价的岗位编号类型。
 */
type EvaluationPost = (typeof EVALUATION_AUTHORIZED_POSTS)[number]

/**
 * 判断岗位编号是否属于评价发布授权岗位。
 * @param id 待判断的岗位编号。
 * @returns 属于授权岗位时返回 true。
 */
const isEvaluationPost = (id: number): id is EvaluationPost =>
  (EVALUATION_AUTHORIZED_POSTS as readonly number[]).includes(id)

/**
 * 检查用户是否有评价权限
 * @param posts 用户岗位列表，格式为 [{post: number}, ...] 或 [number, ...]
 * @returns 是否有评价权限
 */
export const hasEvaluationPermission = (
  posts: ReadonlyArray<{ post?: number } | number> | undefined | null
): boolean => {
  if (!posts || posts.length === 0) return false
  const postIds = posts
    .map((p) => (typeof p === 'number' ? p : p.post))
    .filter((id): id is number => id !== undefined)
  return postIds.some(isEvaluationPost)
}

/**
 * 提取组员岗位 ID 列表（兼容 posts / postIds 两种字段）
 * @param member 组员对象，可来自旧字段 posts 或新字段 postIds。
 * @returns 组员拥有的岗位编号列表。
 */
export const getMemberPostIds = (
  member:
    | Readonly<{
        posts?: ReadonlyArray<{ post?: number }>
        postIds?: ReadonlyArray<number>
      }>
    | null
    | undefined
): number[] => {
  if (!member) return []
  if (member.postIds && member.postIds.length > 0) {
    return [...member.postIds]
  }
  return (member.posts ?? [])
    .map((p) => p.post)
    .filter((id): id is number => id !== undefined)
}

/**
 * 判断组员是否拥有指定岗位
 * @param postId 需要判断的岗位编号。
 * @param member 组员对象，可来自旧字段 posts 或新字段 postIds。
 * @returns 组员拥有该岗位时返回 true。
 */
export const hasMemberPost = (
  postId: number,
  member:
    | Readonly<{
        posts?: ReadonlyArray<{ post?: number }>
        postIds?: ReadonlyArray<number>
      }>
    | null
    | undefined
): boolean => {
  return getMemberPostIds(member).includes(postId)
}

/**
 * 按漫画分类返回可申请的常驻岗位
 * @param category 漫画分类编号；动画分类使用时轴岗位，普通漫画使用嵌字岗位。
 * @returns 当前分类可申请的常驻岗位配置列表。
 */
export const getStationApplyPosts = (
  category: number | undefined
): PostConfig[] => {
  if (category === MANGA_CATEGORY_CONFIG.YURI_ANIMATION.value) {
    return STATIONED_POST.filter((p) => p.id !== POST_CONFIG.LETTERER.id)
  }
  return STATIONED_POST.filter((p) => p.id !== POST_CONFIG.TIMER.id)
}

// 为了向后兼容，导出原来的常量格式
/**
 * 岗位编号到岗位展示名称的映射，兼容旧版数字索引读取。
 */
export const POST_LABELS: Record<number, string> = Object.entries(
  POST_CONFIG
).reduce(
  (acc, [, config]) => {
    acc[config.id] = config.label
    return acc
  },
  {} as Record<number, string>
)

/**
 * 岗位枚举键到岗位编号的映射，兼容旧版字符串索引读取。
 */
export const POST_NUMBERS: Record<string, number> = Object.entries(
  POST_CONFIG
).reduce(
  (acc, [key, config]) => {
    acc[key] = config.id
    return acc
  },
  {} as Record<string, number>
)

/**
 * 岗位接口标识到岗位展示名称的映射。
 */
export const POST_NAME_TO_LABEL: Record<string, string> = {}
Object.values(POST_CONFIG).forEach((config) => {
  POST_NAME_TO_LABEL[config.name] = config.label
})

/**
 * 岗位编号到 Element Plus 标签类型的映射。
 */
export const POST_TAG_TYPES = {
  [POST_CONFIG.TRANSLATOR.id]: 'primary',
  [POST_CONFIG.PROOFREADER.id]: 'success',
  [POST_CONFIG.LETTERER.id]: 'info',
  [POST_CONFIG.REVIEWER.id]: 'warning',
  [POST_CONFIG.OTHER.id]: 'danger',
  [POST_CONFIG.TIMER.id]: 'info',
  [POST_CONFIG.PUBLISHER.id]: 'success'
} as const satisfies Record<number, string>
/**
 * 可直接索引岗位标签类型映射的岗位编号类型。
 */
export type PostTagType = keyof typeof POST_TAG_TYPES

/**
 * 可直接索引岗位名称映射的岗位编号类型。
 */
export type PostLabelType = keyof typeof POST_LABELS

/**
 * Element Plus 标签组件使用的岗位/职阶标签类型。
 */
export type TagType = (typeof POST_TAG_TYPES)[PostTagType]

/**
 * 组员职阶编号，规则与后端 MemberInternEnum 对齐。
 */
export const INTERN = {
  /** 正式组员。 */
  REGULAR: 0,
  /** 实习组员。 */
  INTERN: 1,
  /** 暂不接稿。 */
  NOT_ACCEPTING_WORK: 2,
  /** 退休组员。 */
  RETIRED: 3,
  /** 已退组。 */
  LEFT: 4
} as const satisfies Record<string, number>

/**
 * 组员职阶到 Element Plus 标签类型的映射。
 */
const INTERN_TAG_TYPES = {
  [INTERN.REGULAR]: 'primary',
  [INTERN.INTERN]: 'success',
  [INTERN.NOT_ACCEPTING_WORK]: 'warning',
  [INTERN.RETIRED]: 'info',
  [INTERN.LEFT]: 'danger'
} as const satisfies Record<number, string>

/**
 * 可直接索引组员职阶标签映射的职阶编号类型。
 */
type InternTagType = keyof typeof INTERN_TAG_TYPES

/**
 * 组员职阶编号到中文描述的映射。
 */
export const INTERN_DESCRIPTIONS = {
  [INTERN.REGULAR]: '正式组员',
  [INTERN.INTERN]: '实习组员',
  [INTERN.NOT_ACCEPTING_WORK]: '暂不接稿',
  [INTERN.RETIRED]: '退休组员',
  [INTERN.LEFT]: '已退组'
} as const satisfies Record<number, string>

/**
 * 可直接索引组员职阶描述映射的职阶编号类型。
 */
type InternDescType = keyof typeof INTERN_DESCRIPTIONS

/**
 * 特殊岗位标签自定义颜色映射。
 */
export const INTERN_TAG_COLOR = {
  /** 时轴岗位的自定义 [背景色, 前景色]。 */
  [POST_CONFIG.TIMER.id]: ['#f8edfe', '#c078e3']
} as const satisfies Record<number, [string, string]>

/**
 * 可直接索引特殊岗位颜色映射的岗位编号类型。
 */
type InternTagColorType = keyof typeof INTERN_TAG_COLOR

/**
 * 将字符串或数字转换为数字
 * @param value 字符串或数字
 * @returns 转换后的数字
 */
const normalizeId = (value: string | number | undefined | null): number => {
  if (value === undefined || value === null || value === '') {
    return 0
  }
  return typeof value === 'string' ? Number(value) : value
}

/**
 * 获取岗位标签名称
 * @param postId 岗位ID或字符串
 * @returns 岗位标签名称
 */
export const getPostLabel = (
  postId: PostLabelType | string | number | undefined | null
): string => {
  const numId = normalizeId(postId)
  return POST_LABELS[numId as PostLabelType] ?? ''
}

/**
 * 获取岗位标签类型
 * @param postId 岗位ID或字符串
 * @returns 岗位标签类型 (primary|success|info|warning|danger)
 */
export const getPostType = (
  postId: PostTagType | string | number | undefined | null
): TagType => {
  const numId = normalizeId(postId)
  return POST_TAG_TYPES[numId as PostTagType] ?? 'primary'
}

/**
 * 获取组员职阶标签类型
 * @param intern 职阶ID或字符串
 * @returns 职阶标签类型 (primary|success|info|warning|danger)
 */
export const getInternType = (
  intern: InternTagType | string | number | undefined | null
): TagType => {
  const numId = normalizeId(intern)
  return INTERN_TAG_TYPES[numId as InternTagType] ?? 'primary'
}

/**
 * 获取组员职阶描述
 * @param intern 职阶ID或字符串
 * @returns 职阶描述文本
 */
export const getInternContent = (
  intern: InternDescType | string | number | undefined | null
): string => {
  const numId = normalizeId(intern)
  return INTERN_DESCRIPTIONS[numId as InternDescType] ?? ''
}

/**
 * 获取标签背景色
 * @param key 标签ID或字符串
 * @returns 十六进制背景色
 */
export const getTagBgColor = (key: InternTagColorType | string): string => {
  const numId = normalizeId(key)
  return INTERN_TAG_COLOR[numId as InternTagColorType]
    ? INTERN_TAG_COLOR[numId as InternTagColorType][0]
    : ''
}

/**
 * 获取标签文本色
 * @param key 标签ID或字符串
 * @returns 十六进制文本色
 */
export const getTagTextColor = (key: InternTagColorType | string): string => {
  const numId = normalizeId(key)
  return INTERN_TAG_COLOR[numId as InternTagColorType]
    ? INTERN_TAG_COLOR[numId as InternTagColorType][1]
    : ''
}

/**
 * 获取岗位文件大小限制 (MB)
 * @param postName 岗位接口标识。
 * @returns 文件大小限制 (MB)
 */
export const getFileSizeLimit = (postName: PostName | string): number => {
  const config = Object.values(POST_CONFIG).find((c) => c.name === postName)
  return config?.maxFileSize || DEFAULT_FILE_SIZE_LIMIT
}
